// src/pages/Profile.tsx

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../utils/apiBase';

interface AppAccessData {
  user: {
    id: number;
    name: string;
    gameCreditsTotal: number;
    userId: number;
  };
  subscriptionStatus: {
    hasActiveMonthly: boolean;
    activeCreditCount: number;
    canCreateGame: boolean;
  };
}

const Profile: React.FC = () => {
  const { user, token } = useAuth();
  const [appAccess, setAppAccess] = useState<AppAccessData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAccess = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/games/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to load access data');
        }

        const data = await response.json();
        setAppAccess(data);
      } catch (err) {
        console.error(err);
        setError('Unable to load app access details.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccess();
  }, [token]);

  const hasAccess =
    !!appAccess?.subscriptionStatus.hasActiveMonthly ||
    (appAccess?.subscriptionStatus.activeCreditCount ?? 0) > 0;

  return (
    <div className="section container">
      <h1>Profile</h1>
      <p className="text-secondary">
        Manage your Snowbird Industries account details.
      </p>
      <div className="card" style={{ maxWidth: '640px' }}>
        <h3 style={{ marginBottom: '1rem' }}>Account Details</h3>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <div>
            <strong>Name:</strong> {user?.name}
          </div>
          <div>
            <strong>Email:</strong> {user?.email}
          </div>
          <div>
            <strong>User ID:</strong> {user?.id}
          </div>
        </div>
      </div>
      <div className="card" style={{ maxWidth: '640px', marginTop: '1.5rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>App Access</h3>
        {isLoading ? (
          <p className="text-secondary">Loading access details...</p>
        ) : error ? (
          <p className="text-secondary">{error}</p>
        ) : hasAccess ? (
          <>
            <p className="text-secondary">
              Active access is enabled for your account.
            </p>
            <ul className="list-check" style={{ marginTop: '1rem' }}>
              {appAccess?.subscriptionStatus.hasActiveMonthly && (
                <li>Monthly unlimited subscription active</li>
              )}
              {(appAccess?.subscriptionStatus.activeCreditCount ?? 0) > 0 && (
                <li>
                  Game credits available: {appAccess?.subscriptionStatus.activeCreditCount}
                </li>
              )}
              <li>
                Total unused credits: {appAccess?.user.gameCreditsTotal ?? 0}
              </li>
            </ul>
          </>
        ) : (
          <>
            <p className="text-secondary">
              You have no App access, browse apps and games to signup for fun.
            </p>
            <div className="flex" style={{ gap: '1rem', flexWrap: 'wrap' }}>
              <a href="/apps" className="btn btn-secondary">
                Browse Apps
              </a>
              <a href="/games" className="btn btn-secondary">
                Browse Games
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;
